import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import TaskManager from "@/components/task-management/TaskManager";
import { mockTaskLabels, mockTaskViews } from "@/lib/mock/task-management";
import { TaskLabel } from "@/types/task-management";
import { Button } from "@/components/ui/button";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  CalendarDays, 
  CheckCircle2, 
  ChevronDown, 
  Clock, 
  Cog, 
  FilterX, 
  Inbox, 
  LayoutGrid, 
  List, 
  Plus, 
  Settings, 
  Star, 
  Sun, 
  Tag,
  ChevronLeft
} from "lucide-react";
import CreateTaskDialog from "@/components/task-management/CreateTaskDialog";
import { TaskService } from "@/services/taskService";
import { useToast } from "@/components/ui/use-toast";

export default function TaskManagementPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const view = searchParams.get('view');
  const initialView = view === "kanban" ? "kanban" : "list";
  const { toast } = useToast();
  
  const [labels, setLabels] = useState<TaskLabel[]>([]);
  const [isLoadingLabels, setIsLoadingLabels] = useState(false);
  const [activeSidebarItem, setActiveSidebarItem] = useState("my-day");
  const [currentView, setCurrentView] = useState<"list" | "kanban">(initialView);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  
  // Load task labels
  useEffect(() => {
    async function fetchLabels() {
      setIsLoadingLabels(true);
      try {
        const labelData = await TaskService.getTaskLabels();
        setLabels(labelData);
      } catch (error) {
        console.error("Error fetching labels:", error);
        setLabels(mockTaskLabels);
      } finally {
        setIsLoadingLabels(false);
      }
    }
    
    fetchLabels();
  }, []);
  
  // Generate statistics (these would be real in a production app)
  const stats = {
    today: 5,
    upcoming: 12,
    overdue: 2,
    completed: 28,
    total: 47
  };
  
  // Handler for sidebar items
  const handleSidebarItemClick = (item: string) => {
    setActiveSidebarItem(item);
    
    // Aqui aplicaríamos a lógica de filtro baseada na seleção
  };
  
  const handleViewChange = (view: "list" | "kanban") => {
    setCurrentView(view);
    // Update URL without reloading the page
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set('view', view);
    window.history.pushState({}, '', newUrl.toString());
  };
  
  return (
    <div className="container mx-auto py-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
        <div>
          <CardTitle>Gerenciamento de Tarefas</CardTitle>
          <CardDescription>Organize suas tarefas pessoais e acompanhe atividades de playbooks</CardDescription>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            className="gap-1" 
            onClick={() => setSidebarVisible(!sidebarVisible)}
            title={sidebarVisible ? "Ocultar painel lateral" : "Mostrar painel lateral"}
          >
            <ChevronLeft className={`h-4 w-4 transition-transform ${sidebarVisible ? "" : "rotate-180"}`} />
            <span className="hidden sm:inline">{sidebarVisible ? "Ocultar painel" : "Mostrar painel"}</span>
          </Button>
          
          <CreateTaskDialog 
            title="Nova tarefa"
            onTaskCreated={(taskData) => {
              toast({
                title: "Tarefa criada",
                description: "A nova tarefa foi adicionada com sucesso."
              });
            }}
            trigger={
              <Button 
                variant="default" 
                size="sm" 
                className="gap-1"
              >
                <Plus className="h-4 w-4" />
                <span>Nova Tarefa</span>
              </Button>
            }
          />
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Sidebar - usa classes CSS para controlar visibilidade e animação */}
        <div 
          className={`col-span-12 md:col-span-4 lg:col-span-3 transition-all duration-300 ease-in-out
            ${sidebarVisible ? 'opacity-100 max-h-[2000px]' : 'opacity-0 max-h-0 overflow-hidden md:hidden'}
          `}
        >
          <Card>
            <CardContent className="p-3">
              <div className="space-y-1.5">
                <div className="font-extrabold text-sm text-muted-foreground mb-2">
                  VISTAS PADRÃO
                </div>
                
                <Button 
                  variant={activeSidebarItem === "my-day" ? "default" : "ghost"} 
                  className="w-full justify-start"
                  onClick={() => handleSidebarItemClick("my-day")}
                >
                  <Sun className="mr-2 h-4 w-4 text-amber-500" />
                  <span>Meu dia</span>
                  {stats.today > 0 && (
                    <Badge className="ml-auto" variant="secondary">{stats.today}</Badge>
                  )}
                </Button>
                
                <Button 
                  variant={activeSidebarItem === "important" ? "default" : "ghost"} 
                  className="w-full justify-start"
                  onClick={() => handleSidebarItemClick("important")}
                >
                  <Star className="mr-2 h-4 w-4 text-amber-500" />
                  <span>Importantes</span>
                </Button>
                
                <Button 
                  variant={activeSidebarItem === "all" ? "default" : "ghost"} 
                  className="w-full justify-start"
                  onClick={() => handleSidebarItemClick("all")}
                >
                  <Inbox className="mr-2 h-4 w-4 text-blue-500" />
                  <span>Todas as tarefas</span>
                  <Badge className="ml-auto" variant="secondary">{stats.total}</Badge>
                </Button>
                
                <Button 
                  variant={activeSidebarItem === "upcoming" ? "default" : "ghost"} 
                  className="w-full justify-start"
                  onClick={() => handleSidebarItemClick("upcoming")}
                >
                  <CalendarDays className="mr-2 h-4 w-4 text-indigo-500" />
                  <span>Próximas</span>
                  {stats.upcoming > 0 && (
                    <Badge className="ml-auto" variant="secondary">{stats.upcoming}</Badge>
                  )}
                </Button>
                
                <Button 
                  variant={activeSidebarItem === "overdue" ? "default" : "ghost"} 
                  className="w-full justify-start"
                  onClick={() => handleSidebarItemClick("overdue")}
                >
                  <Clock className="mr-2 h-4 w-4 text-red-500" />
                  <span>Atrasadas</span>
                  {stats.overdue > 0 && (
                    <Badge className="ml-auto" variant="destructive">{stats.overdue}</Badge>
                  )}
                </Button>
                
                <Button 
                  variant={activeSidebarItem === "completed" ? "default" : "ghost"} 
                  className="w-full justify-start"
                  onClick={() => handleSidebarItemClick("completed")}
                >
                  <CheckCircle2 className="mr-2 h-4 w-4 text-green-500" />
                  <span>Concluídas</span>
                  <Badge className="ml-auto" variant="secondary">{stats.completed}</Badge>
                </Button>
              </div>
              
              <Separator className="my-4" />
              
              <div>
                <div className="font-extrabold text-sm text-muted-foreground mb-2">
                  ETIQUETAS
                </div>
                <div className="space-y-1">
                  {labels.map(label => (
                    <Button 
                      key={label.id}
                      variant={activeSidebarItem === `label-${label.id}` ? "default" : "ghost"} 
                      className="w-full justify-start"
                      onClick={() => handleSidebarItemClick(`label-${label.id}`)}
                    >
                      <div 
                        className="w-3 h-3 rounded-full mr-2" 
                        style={{backgroundColor: label.color}}
                      />
                      <span>{label.name}</span>
                    </Button>
                  ))}
                  
                  <Button 
                    variant="ghost" 
                    className="w-full justify-start text-muted-foreground"
                    onClick={() => navigate("/task-management/labels")}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    <span>Nova etiqueta</span>
            </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Main Content */}
        <div className={`col-span-12 ${sidebarVisible ? 'md:col-span-8 lg:col-span-9' : 'md:col-span-12 lg:col-span-12'}`}>
          <Card className="shadow-sm">
            <CardHeader className="pb-2 pt-3 px-4">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    {activeSidebarItem === "my-day" && <Sun className="h-5 w-5 text-amber-500" />}
                    {activeSidebarItem === "important" && <Star className="h-5 w-5 text-amber-500" />}
                    {activeSidebarItem === "all" && <Inbox className="h-5 w-5 text-blue-500" />}
                    {activeSidebarItem === "upcoming" && <CalendarDays className="h-5 w-5 text-indigo-500" />}
                    {activeSidebarItem === "overdue" && <Clock className="h-5 w-5 text-red-500" />}
                    {activeSidebarItem === "completed" && <CheckCircle2 className="h-5 w-5 text-green-500" />}
                    {activeSidebarItem === "my-day" && "Meu dia"}
                    {activeSidebarItem === "important" && "Tarefas importantes"}
                    {activeSidebarItem === "all" && "Todas as tarefas"}
                    {activeSidebarItem === "upcoming" && "Tarefas próximas"}
                    {activeSidebarItem === "overdue" && "Tarefas atrasadas"}
                    {activeSidebarItem === "completed" && "Tarefas concluídas"}
                    {activeSidebarItem.startsWith("label-") && (
                      <>
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{
                            backgroundColor: labels.find(
                              l => l.id === activeSidebarItem.replace("label-", "")
                            )?.color || "#666"
                          }}
                        />
                        {labels.find(l => l.id === activeSidebarItem.replace("label-", ""))?.name || "Etiqueta"}
                      </>
                    )}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    {activeSidebarItem === "my-day" && "Tarefas para focar hoje"}
                    {activeSidebarItem === "important" && "Tarefas marcadas como importantes"}
                    {activeSidebarItem === "all" && "Visualize e gerencie todas as suas tarefas"}
                    {activeSidebarItem === "upcoming" && "Tarefas com datas de vencimento próximas"}
                    {activeSidebarItem === "overdue" && "Tarefas que passaram do prazo de vencimento"}
                    {activeSidebarItem === "completed" && "Tarefas que você já concluiu"}
                    {activeSidebarItem.startsWith("label-") && "Tarefas com esta etiqueta"}
                  </CardDescription>
                </div>
                
                <div className="flex items-center gap-2">
                  <Tabs
                    value={currentView}
                    onValueChange={(v) => handleViewChange(v as "list" | "kanban")}
                    className="w-full"
                  >
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="list" className="flex items-center gap-1">
                        <List className="h-4 w-4" />
                        <span className="sr-only sm:not-sr-only">Lista</span>
                      </TabsTrigger>
                      <TabsTrigger value="kanban" className="flex items-center gap-1">
                        <LayoutGrid className="h-4 w-4" />
                        <span className="sr-only sm:not-sr-only">Kanban</span>
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="p-4 pt-2">
              <TaskManager 
                initialView={currentView} 
                savedViews={mockTaskViews} 
              />
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Statistics Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <Card>
          <CardHeader className="pb-2 p-3">
            <CardTitle className="text-sm font-extrabold text-muted-foreground">RESUMO DE ATIVIDADES</CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0">
            <div className="flex justify-between mb-4">
              <div>
                <div className="text-2xl font-extrabold">{stats.total}</div>
                <div className="text-xs text-muted-foreground">Total de tarefas</div>
              </div>
              <div>
                <div className="text-2xl font-extrabold text-green-600">{stats.completed}</div>
                <div className="text-xs text-muted-foreground">Concluídas</div>
              </div>
              <div>
                <div className="text-2xl font-extrabold text-red-600">{stats.overdue}</div>
                <div className="text-xs text-muted-foreground">Atrasadas</div>
              </div>
            </div>
            <div className="w-full bg-muted rounded-full h-2.5">
              <div 
                className="bg-green-600 h-2.5 rounded-full" 
                style={{ width: `${(stats.completed / stats.total) * 100}%` }}
              ></div>
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {Math.round((stats.completed / stats.total) * 100)}% completo
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 p-3">
            <CardTitle className="text-sm font-extrabold text-muted-foreground">TAREFAS PARA HOJE</CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0">
            <div className="flex items-center">
              <Sun className="h-8 w-8 text-amber-500 mr-3" />
              <div>
                <div className="text-2xl font-extrabold">{stats.today}</div>
                <div className="text-xs text-muted-foreground">Restantes para hoje</div>
              </div>
            </div>
            <Button 
              variant="ghost" 
              className="w-full mt-3 text-muted-foreground hover:text-foreground"
              onClick={() => handleSidebarItemClick("my-day")}
            >
              <Sun className="mr-2 h-4 w-4 text-amber-500" />
              <span>Ver tarefas do dia</span>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 p-3">
            <CardTitle className="text-sm font-extrabold text-muted-foreground">PRÓXIMOS VENCIMENTOS</CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0 space-y-2">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-red-500" />
                <span className="text-sm">Implementar feature X</span>
              </div>
              <Badge variant="outline">Hoje</Badge>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-500" />
                <span className="text-sm">Revisar documentação</span>
              </div>
              <Badge variant="outline">Amanhã</Badge>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Atualizar relatório</span>
              </div>
              <Badge variant="outline">Em 3 dias</Badge>
            </div>
            <Button 
              variant="ghost" 
              className="w-full mt-2 text-muted-foreground hover:text-foreground"
              onClick={() => handleSidebarItemClick("upcoming")}
            >
              <CalendarDays className="mr-2 h-4 w-4 text-indigo-500" />
              <span>Ver todas</span>
            </Button>
        </CardContent>
      </Card>
      </div>
    </div>
  );
} 